import { useRef, useState, type KeyboardEvent } from 'react';
import { Input } from '@/uicomponents';
import styles from './inline-edit.module.css';

export interface InlineEditProps {
  value: string;
  onCommit: (next: string) => void;
  placeholder?: string;
  ariaLabel: string;
  maxLength?: number;
  /** Focus the input when it mounts — for click-to-edit flows. */
  focusOnMount?: boolean;
  /** Fired when the editing session ends (commit-on-blur or Escape-revert). */
  onDone?: () => void;
}

export const InlineEdit = ({
  value,
  onCommit,
  placeholder,
  ariaLabel,
  maxLength,
  focusOnMount,
  onDone,
}: InlineEditProps) => {
  const [draft, setDraft] = useState(value);
  // blur() fires synchronously inside the keydown handler — before React applies
  // any setDraft — so a keyboard-settled session (Enter commit / Escape revert)
  // must tell the blur handler its stale-closure draft is already resolved.
  const settledByKeyRef = useRef(false);

  const commit = () => {
    if (draft !== value) {
      onCommit(draft);
    }
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      settledByKeyRef.current = true;
      commit();
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      settledByKeyRef.current = true;
      setDraft(value);
      e.currentTarget.blur();
    }
  };

  const onBlur = () => {
    if (!settledByKeyRef.current) {
      commit();
    }
    settledByKeyRef.current = false;
    onDone?.();
  };

  return (
    <Input
      className={styles.inlineEdit}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={onBlur}
      onKeyDown={onKey}
      placeholder={placeholder}
      aria-label={ariaLabel}
      maxLength={maxLength}
      // eslint-disable-next-line jsx-a11y/no-autofocus -- edit mode entered by user intent
      autoFocus={focusOnMount}
      variant='borderless'
    />
  );
};
